import type { Flows } from '@/api/types';
import { BarList } from '@/components/BarList';
import { Callout } from '@/components/Callout';
import { Sunburst } from '@/components/Sunburst';
import { formatCompact } from '@/components/TrendChart';

/**
 * The qualifier that has to travel with every path number in this product.
 *
 * `audit_events` records write actions — reading a record leaves no row anywhere — so these are the
 * moves that CHANGED something. Rendering "the path" without this sentence describes a thing the
 * data cannot see, and the difference matters most for exactly the read-heavy roles (support,
 * analysts) whose work would appear to be nothing at all.
 */
export function PathScopeNote({ gapMinutes }: { gapMinutes: number }) {
  return (
    <p className="t-caption subtle" style={{ margin: '8px 0 0' }}>
      Built from recorded changes, so time spent reading does not appear. A working session is
      activity with no gap of {gapMinutes} minutes or more — not a login session.
    </p>
  );
}

/**
 * Top transitions plus the entry-point Sunburst.
 *
 * The ranked list is a `BarList` in ONE hue: every row is the same measure (moves) sliced by which
 * pair it was, so a per-row colour would imply an identity that is not there. The Sunburst is the
 * only place a ramp is legal here, and only because it colours by DEPTH — an ordered category.
 *
 * No force-directed graph. A hairball looks impressive and is unreadable past a dozen nodes; the
 * ranked list answers the question that was actually asked.
 */
export function FlowsPanel({ flows, title }: { flows: Flows; title: string }) {
  const ranked = flows.transitions.map((t) => ({
    label: `${t.from_display_name} → ${t.to_display_name}`,
    value: t.count,
  }));

  const hasAnything = flows.total_transitions > 0;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>{title}</h2>
        {hasAnything && (
          <span className="t-label subtle">
            {formatCompact(flows.total_transitions)} moves
          </span>
        )}
      </div>

      {!hasAnything ? (
        <>
          <div className="empty-state t-body">
            No feature-to-feature moves recorded in this window.
          </div>
          <PathScopeNote gapMinutes={flows.working_session_gap_minutes} />
        </>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
            <div>
              <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>Most common moves</h3>
              <BarList rows={ranked} limit={8} emptyLabel="No moves between two different features." />
            </div>

            <div>
              <h3 className="t-label muted" style={{ margin: '0 0 8px' }}>Where sessions start</h3>
              {flows.entry_paths.length === 0 ? (
                <div className="empty-state t-body">No session openings recorded.</div>
              ) : (
                <Sunburst
                  paths={flows.entry_paths.map((p) => ({
                    steps: p.display_names,
                    value: p.sessions,
                  }))}
                  maxDepth={4}
                  centerLabel="sessions"
                  valueLabel="Sessions"
                  /* These rings are FEATURES. The component's default noun is "Page", written for
                     the site-journey caller, and left alone it labels this chart with a vocabulary
                     from a different product surface. */
                  stepNoun="Feature"
                />
              )}
            </div>
          </div>

          {/* The ranking is a SUBSET, and saying so is the difference between a chart and a claim.
              Self-moves and the folded tail are both real counted work that the bars above exclude,
              so without this line the bars read as the whole of what people did. */}
          {(flows.self_transitions > 0 || flows.folded_transitions > 0) && (
            <p className="t-caption subtle" style={{ margin: '12px 0 0' }}>
              Excluded from the ranking:
              {flows.self_transitions > 0 && (
                <> {formatCompact(flows.self_transitions)} repeat moves within the same feature</>
              )}
              {flows.self_transitions > 0 && flows.folded_transitions > 0 && ' ·'}
              {flows.folded_transitions > 0 && (
                <> {formatCompact(flows.folded_transitions)} in a folded tail beyond the cap</>
              )}
              . Both are counted in the total.
            </p>
          )}

          <PathScopeNote gapMinutes={flows.working_session_gap_minutes} />
        </>
      )}
    </div>
  );
}

/** Shown when an agent has no shape row — an absence, not a person who did nothing. */
export function ShapeNotCollected() {
  return (
    <Callout tone="warning" title="No session shape for this person">
      The collector has not landed a working-session rollup for this agent yet, so depth and entry
      points are unknown rather than zero.
    </Callout>
  );
}
